import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
//deploy

function AIReportsModal({ creatorId, accountFilter, accounts, onClose }) {
  // Every report is about ONE account. Start from the page's filter (or the
  // only account); with several accounts and no filter the user picks one
  // here before anything can be generated.
  // First account by default — every Performance/Discovery run is about one account
  const [acct, setAcct] = useState(accountFilter || accounts?.[0]?.id || null);

  const [activeTab, setActiveTab] = useState('performance');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState(null);
  const [discoveryReport, setDiscoveryReport] = useState(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(null);
  const [discoveryLoaded, setDiscoveryLoaded] = useState(false);
  const [perfReports, setPerfReports] = useState([]);
  const [selectedPerfReport, setSelectedPerfReport] = useState('');
  // "Configure discovery" filters — pick any combination. Verified /
  // Non-verified / Dating are groups of subs to include; AI tolerable drops
  // subs whose rules ban AI-generated content.
  // Pre-filled from the selected account's strategy (its soft preferences);
  // clicking a chip changes THIS run only. (They used to come from browser
  // storage shared by all accounts, so the strategy never showed up here.)
  const [discoveryFilters, setDiscoveryFilters] = useState({ verified: true, non_verified: true, ai_tolerable: false, dating: false });
  const [filtersFromStrategy, setFiltersFromStrategy] = useState(null);
  // Only chips someone actually clicked are sent; untouched = the backend
  // uses the account's strategy (same as the Planner's Run Discovery)
  const [filtersTouched, setFiltersTouched] = useState(false);
  const toggleFilter = (k) => setDiscoveryFilters(prev => {
    const next = { ...prev, [k]: !prev[k] };
    if (!next.verified && !next.non_verified && !next.dating) return prev; // keep at least one group
    setFiltersTouched(true);
    return next;
  });
  const [contentReport, setContentReport] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentReportCount, setContentReportCount] = useState(0);

  // Prerequisite readiness for the run-order nudges
  const hasPerf = perfReports.length > 0;
  const hasContent = contentReportCount > 0;
  


  const selectedAccount = acct ? accounts.find(a => a.id === acct) : null;
  // When the automatic jobs last ran — they failed silently for 12 weeks
  const [jobStatus, setJobStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/reports/jobs/status`).then(r => { if (alive) setJobStatus(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Which days the next report would cover, or when it is ready. Reports are
  // run by a person: 7 new days of settled clicks (Performance), 30 (Content),
  // never the same days twice (backend/helpers/reportWindow.js)
  const [ready, setReady] = useState(null);
  const loadReady = () => {
    if (!creatorId) return;
    const reqAcct = acct;
    axios.get(`${API}/api/reports/ready?creator_id=${creatorId}${acct ? `&account_id=${acct}` : ''}`)
      .then(r => { if (reqAcct === acctRef.current) setReady(r.data); }).catch(() => setReady(null));
  };
  const dayLabel = (d) => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
  // [disabled, button suffix, hint line] for a report kind
  const readiness = (kind) => {
    const w = ready?.[kind];
    if (!w) return [false, '', null];
    if (w.ready) return [false, ` (${w.label})`, `Covers ${w.label}: ${w.days} whole days${kind === 'performance' && !w.views_only ? ' of clicks' : ''}, leaving out the last 2 ${kind === 'performance' && !w.views_only ? 'synced ' : ''}days while they settle.`];
    return [true, w.next_ready_on ? ` — ready ${dayLabel(w.next_ready_on)}` : '', `⏳ ${w.why_not || 'Not ready yet.'}`];
  };
  const creatorRef = useRef(creatorId);
  creatorRef.current = creatorId;
  const acctRef = useRef(acct);
  acctRef.current = acct; // for answers that arrive after a switch
  useEffect(() => {
    if (!acct) return;
    let cancelled = false;
    axios.get(`${API}/api/accounts/${acct}/strategy`).then(res => {
      if (cancelled) return;
      const t = res.data?.current?.targeting || {};
      const f = { verified: t.verified !== false, non_verified: t.non_verified !== false, ai_tolerable: !!t.ai && t.ai !== 'any', dating: !!t.dating };
      if (!f.verified && !f.non_verified && !f.dating) { f.verified = true; f.non_verified = true; }
      setDiscoveryFilters(f);
      setFiltersTouched(false);
      setFiltersFromStrategy(res.data?.is_default ? 'default' : 'strategy');
    }).catch(() => setFiltersFromStrategy(null));
    return () => { cancelled = true; };
  }, [acct]);

  // Load latest report on open. Also runs when the account changes — so
  // invalidate the lazily-loaded tabs (Discovery / Action Plan) here, otherwise
  // their one-shot latches keep showing the previous account's data.
  useEffect(() => {
    if (!creatorId) return;
    // Clear the previous account's report first — otherwise its verdicts
    // stayed on screen (and its report id was sent to Discovery) under the
    // new account's name.
    setReport(null);
    setHistory([]);
    setPerfReports([]);
    setSelectedPerfReport('');
    loadLatest();
    loadHistory();
    loadPerfReports();
    loadContentCount();
    setContentReport(null);
    loadContentLatest(); // open the latest Content report straight away
    setReady(null);
    loadReady();
    setDiscoveryLoaded(false);
    setDiscoveryReport(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, acct]);

  const loadContentCount = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/performance/history?creator_id=${creatorId}&report_type=content&limit=1`);
      setContentReportCount((res.data || []).length);
    } catch (err) {
      console.error('Load content count error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'discovery' && creatorId && !discoveryLoaded) {
      loadDiscoveryLatest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, creatorId, acct]);



  const loadLatest = async () => {
    if (!acct) return;
    setLoadingLatest(true);
    try {
      const reqAcct = acct;
      const res = await axios.get(`${API}/api/reports/performance/latest?creator_id=${creatorId}&account_id=${reqAcct}`);
      if (reqAcct === acctRef.current) setReport(res.data || null);
    } catch (err) {
      console.error('Load latest report error:', err);
    }
    setLoadingLatest(false);
  };

  const loadHistory = async () => {
    try {
      // Performance history only (content / old action-plan rows used to show
      // here and render as a Performance report)
      const res = await axios.get(`${API}/api/reports/performance/history?creator_id=${creatorId}&report_type=performance&limit=10${acct ? `&account_id=${acct}` : ''}`);
      setHistory(res.data || []);
    } catch (err) {
      console.error('Load history error:', err);
    }
  };

  const loadDiscoveryLatest = async () => {
    if (discoveryLoaded) return;
    try {
      const acc = acct ? `&account_id=${acct}` : '';
      const res = await axios.get(`${API}/api/discovery/history?creator_id=${creatorId}&limit=1${acc}`);
      if (res.data?.length > 0) {
        const detail = await axios.get(`${API}/api/discovery/detail/${res.data[0].id}`);
        if (detail.data) setDiscoveryReport(detail.data);
      }
    } catch (err) {
      console.error('Load discovery error:', err);
    }
    setDiscoveryLoaded(true);
  };

  const loadPerfReports = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/performance/history?creator_id=${creatorId}&report_type=performance&limit=10${acct ? `&account_id=${acct}` : ''}`);
      setPerfReports(res.data || []);
      // newest report of THIS account (reset on account switch / new report)
      setSelectedPerfReport(res.data?.[0]?.id || '');
    } catch (err) {
      console.error('Load perf reports error:', err);
    }
  };

  const loadReport = async (reportId) => {
    try {
      const res = await axios.get(`${API}/api/reports/performance/detail/${reportId}`);
      if (res.data) setReport(res.data);
      setShowHistory(false);
    } catch (err) {
      console.error('Load report error:', err);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/api/reports/performance/detail/${reportId}`);
      setHistory(prev => prev.filter(h => h.id !== reportId));
      if (report?.id === reportId) setReport(null);
    } catch (err) {
      console.error('Delete report error:', err);
    }
  };

  const generateReport = async () => {
    if (!acct) { alert('Pick an account first — every report is about one account.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/api/reports/performance`, { creator_id: creatorId, account_id: acct });
      setReport({ report_data: res.data, created_at: new Date().toISOString(), period_from: res.data?.period?.from, period_to: res.data?.period?.to });
      loadHistory();
      loadPerfReports(); // Discovery should use the report just made
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    loadReady();
    setLoading(false);
  };

  // "Showing the report from …" next to each Generate button
  const shortDate = (d) => new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const dayOnly = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const reportStamp = (createdAt, from, to, emptyText) => {
    if (!createdAt) return <span style={{ fontSize: '11px', color: '#555' }}>{emptyText || 'No report yet'}</span>;
    const ageDays = Math.floor((Date.now() - new Date(createdAt).getTime()) / 864e5);
    return (
      <span title={new Date(createdAt).toLocaleString()} style={{ fontSize: '11px', color: ageDays > 14 ? '#f59e0b' : '#888' }}>
        🕒 Showing the report from <b style={{ color: ageDays > 14 ? '#f59e0b' : '#bbb' }}>{shortDate(createdAt)}</b>
        {from && to ? ` · covers ${dayOnly(from)}–${dayOnly(to)}` : ''}
        {ageDays > 0 ? ` · ${ageDays} day${ageDays === 1 ? '' : 's'} old` : ' · today'}
      </span>
    );
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };


  // Extract report data (handle both direct and nested structures)
  const rd = report?.report_data || report || {};
  const ai = rd.ai_analysis || rd.report || null;
  const data = rd.period ? rd : (rd.input_data || {});

  // ─── Tab styles ───
  const tabStyle = (active) => ({
    padding: '10px 24px', border: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600', borderRadius: '8px 8px 0 0',
    background: active ? '#1a1a1a' : 'transparent',
    color: active ? '#ff4500' : '#555',
    borderBottom: active ? '2px solid #ff4500' : '2px solid transparent'
  });


  // ─── Performance Report Renderer ───
  const renderPerformanceReport = () => {
    if (loadingLatest) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading latest report...</div>;

    return (
      <div>
        {/* Controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          {selectedAccount && (
            <span style={{ fontSize: '13px', color: '#888' }}>u/{selectedAccount.reddit_username}</span>
          )}
          <span style={{ marginLeft: 'auto' }}>{reportStamp(report?.created_at, report?.period_from || report?.report_data?.period?.from, report?.period_to || report?.report_data?.period?.to, 'No Performance report for this account yet')}</span>
          <button
            onClick={generateReport}
            disabled={loading || readiness('performance')[0]}
            title={readiness('performance')[2] || ''}
            style={{
              padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: loading ? 'wait' : readiness('performance')[0] ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '600',
              background: loading || readiness('performance')[0] ? '#333' : '#ff4500', color: readiness('performance')[0] ? '#888' : '#fff'
            }}
          >
            {loading ? '🔄 Generating...' : `📊 Generate report${readiness('performance')[1]}`}
          </button>
          {history.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #333', background: 'transparent', color: '#888', fontSize: '12px', cursor: 'pointer' }}
            >
              History ({history.length})
            </button>
          )}
        </div>

        {readiness('performance')[2] && !loading && (
          <div style={{ fontSize: '11px', color: readiness('performance')[0] ? '#888' : '#60a5fa', margin: '-8px 0 12px' }}>{readiness('performance')[2]}</div>
        )}

        {/* Data check: how completely this report's window was measured */}
        {!loading && rd?.data_coverage && (
          <div title="The report only judges subs on a window whose views were read and whose clicks are synced. When the check fails, the report says what is missing and makes no Remove suggestions."
            style={{ fontSize: '11px', margin: '-8px 0 14px', color: rd.data_coverage.ok ? '#22c55e' : '#f59e0b' }}>
            {rd.data_coverage.ok ? '✓ Data check passed' : '⚠ Incomplete data — this report makes no Remove suggestions'}
            {rd.data_coverage.measured_pct != null ? ` · ${rd.data_coverage.measured_pct}% of posts have a views reading` : ''}
            {rd.data_coverage.early_readings_pct ? ` · ${rd.data_coverage.early_readings_pct}% read before 48h (estimated to final)` : ''}
            {rd.data_coverage.clicks_complete === false ? ' · clicks not synced to the end' : ''}
            {!rd.data_coverage.ok && rd.data_coverage.problems?.length ? <div style={{ color: '#aaa', marginTop: '2px' }}>{rd.data_coverage.problems.join(' · ')}</div> : null}
          </div>
        )}

        {error && <div style={{ padding: '12px', background: '#2a0a0a', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}

        {loading && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#ff4500', marginBottom: '8px' }}>Analyzing your data with Claude...</div>
            <div style={{ fontSize: '13px', color: '#666' }}>This takes 15-30 seconds. Gathering clicks, sessions, subreddit performance...</div>
          </div>
        )}

        {/* Click data freshness — reports run on synced bio-link clicks */}
        {data?.click_sync && !data.click_sync.synced && data.click_sync.error && (
          <div style={{ padding: '8px 12px', background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: '8px', color: '#fca5a5', fontSize: '12px', marginBottom: '12px' }}>
            ⚠ Click sync failed before this report ({data.click_sync.error}). Click numbers may be stale.
          </div>
        )}
        {data?.attribution_quality && data.attribution_quality.level === 'none' && (
          <div style={{ padding: '8px 12px', background: '#1a1a0a', border: '1px solid #5a4400', borderRadius: '8px', color: '#f59e0b', fontSize: '12px', marginBottom: '12px' }}>
            ⚠ No click data in this period{data.attribution_quality.tracked_link ? '' : ' — no tracked bio link on this account'}. Verdicts are based on views and upvotes only.
          </div>
        )}
        {/* History dropdown */}
        {showHistory && (
          <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '8px', marginBottom: '16px' }}>
            {history.map(h => (
              <div
                key={h.id}
                style={{ padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#aaa' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#222'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span onClick={() => loadReport(h.id)} style={{ flex: 1 }}>
                  {formatDate(h.created_at)}
                  <span style={{ color: h.account_id ? '#3b82f6' : '#f59e0b', marginLeft: '8px', fontSize: '11px' }}>
                    {h.account_id ? 'u/' + ((accounts || []).find(a => a.id === h.account_id)?.reddit_username || '?') : 'all accounts (old)'}
                  </span>
                </span>
                <span style={{ color: '#666', marginRight: '8px' }}>{h.post_count || '?'} posts</span>
                <button onClick={(e) => { e.stopPropagation(); deleteReport(h.id); }}
                  style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: '12px', padding: '2px 6px' }}
                  onMouseEnter={e => { e.target.style.color = '#ef4444'; }}
                  onMouseLeave={e => { e.target.style.color = '#555'; }}
                >✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Report content */}
        {!loading && ai && renderAIReport(ai, data)}
        {!loading && !ai && !error && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#555' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
            <div style={{ fontSize: '15px', marginBottom: '8px' }}>No report generated yet</div>
            <div style={{ fontSize: '13px', color: '#444' }}>Click "Generate report" (or Run on the Planner's card) for an AI analysis of the last 7 ready days.</div>
          </div>
        )}

        {report?.created_at && (
          <div style={{ fontSize: '11px', color: '#444', marginTop: '16px', textAlign: 'right' }}>
            Generated: {formatDate(report.created_at)}
          </div>
        )}
      </div>
    );
  };

  // ─── AI Report Renderer ───
  const renderAIReport = (ai, data) => {
    if (ai.error) return <div style={{ color: '#ef4444', padding: '20px' }}>Report generation failed: {ai.error}</div>;

    const scoreColor = (ai.overall_score || 0) >= 7 ? '#22c55e' : (ai.overall_score || 0) >= 4 ? '#f59e0b' : '#ef4444';
    const trendColors = { improving: '#22c55e', stable: '#f59e0b', declining: '#ef4444' };

    return (
      <div>
        {/* Score + Summary */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', alignItems: 'flex-start' }}>
          <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: '16px 20px', textAlign: 'center', minWidth: '80px' }}>
            <div style={{ fontSize: '36px', fontWeight: '700', color: scoreColor }}>{ai.overall_score || '?'}</div>
            <div style={{ fontSize: '11px', color: '#666' }}>/ 10</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', color: '#ccc', lineHeight: '1.6', marginBottom: '8px' }}>{ai.summary}</div>
            {ai.trend && <span style={{ fontSize: '12px', color: trendColors[ai.trend] || '#666', fontWeight: '600' }}>Trend: {ai.trend}</span>}
          </div>
        </div>

        {/* Since last report */}
        {ai.since_last_report && (
          <div style={{ background: '#0a0f1a', border: '1px solid #1d4ed840', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>↔ Since last report</div>
            <div style={{ fontSize: '13px', color: '#ccc', lineHeight: '1.6' }}>{ai.since_last_report}</div>
          </div>
        )}

        {/* Verdict scorecard: how the previous report's calls turned out (backend/helpers/verdictScorecard.js) */}
        {rd.scorecard?.checked > 0 && (
          <div title={rd.scorecard.checked_report_date ? `The ${rd.scorecard.checked_report_date} report's verdicts graded on what was measured since: drop wrong = the sub stands at 50%+ of the account's average post and did not fall; reduce wrong = it recovered 20%+; daily/rotate wrong = it fell under 50%; too early = under 2 posts measured since` : ''}
            style={{ fontSize: '12px', color: '#aaa', margin: '-8px 0 14px', lineHeight: '1.6' }}>
            Last week's calls: <span style={{ color: '#22c55e' }}>{rd.scorecard.held} held</span>, <span style={{ color: rd.scorecard.wrong ? '#ef4444' : '#888' }}>{rd.scorecard.wrong} wrong</span>, {rd.scorecard.too_early} too early
            {rd.scorecard.wrong_examples?.length > 0 && <span style={{ color: '#888' }}> · wrong: {rd.scorecard.wrong_examples.join(', ')}</span>}
            {rd.scorecard.repeatedly_wrong?.length > 0 && <span style={{ color: '#f59e0b' }}> · {rd.scorecard.repeatedly_wrong.map(r => `"${r.verdict}" wrong ${r.wrong}× in the last 3 reports`).join(', ')}</span>}
          </div>
        )}

        {/* Click Analysis */}
        {ai.click_analysis && (
          <div style={{ background: '#1a1a1a', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #2a2a2a' }}>
            <div style={{ fontSize: '12px', color: '#ff4500', fontWeight: '600', marginBottom: '8px' }}>Click analysis</div>
            <div style={{ fontSize: '13px', color: '#ccc', lineHeight: '1.6' }}>{ai.click_analysis.insight || ai.click_attribution_notes}</div>
            {ai.click_analysis.best_session && (
              <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                Best session: {ai.click_analysis.best_session.date} — {ai.click_analysis.best_session.clicks} clicks. {ai.click_analysis.best_session.why}
              </div>
            )}
            {ai.click_analysis.recommendations?.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {ai.click_analysis.recommendations.map((rec, i) => (
                  <div key={i} style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>→ {rec}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subreddit Verdicts */}
        {ai.subreddit_verdicts?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#aaa', fontWeight: '600', marginBottom: '8px' }}>Subreddit verdicts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {ai.subreddit_verdicts.map((sv, i) => {
                const verdictColors = {
                  daily: '#22c55e', rotate: '#3b82f6',
                  reduce: '#f59e0b', test: '#3b82f6',
                  keep: '#22c55e', increase: '#22c55e',
                  test_more: '#3b82f6',
                  monitor: '#f59e0b', decrease: '#f59e0b',
                  drop: '#ef4444'
                };
                const color = verdictColors[sv.verdict] || '#888';
                const trendIcon = { improving: '↗', declining: '↘', stable: '→', new: '✦' }[sv.trend];
                const trendColor = { improving: '#22c55e', declining: '#ef4444', stable: '#888', new: '#8b5cf6' }[sv.trend] || '#555';
                return (
                  <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 12px', background: '#1a1a1a', borderRadius: '6px', border: '1px solid #222' }}>
                    {trendIcon && (
                      <span title={sv.trend === 'new' ? 'new this report' : `trend: ${sv.trend}`}
                        style={{ fontSize: '13px', color: trendColor, fontWeight: '700', width: '14px', textAlign: 'center', flexShrink: 0 }}>{trendIcon}</span>
                    )}
                    <span style={{ color: '#ff4500', fontWeight: '600', fontSize: '13px', minWidth: '140px' }}>{sv.subreddit}</span>
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px',
                      background: color + '15', color: color, border: `1px solid ${color}40`,
                      minWidth: '70px', textAlign: 'center'
                    }}>{String(sv.verdict || '?').replace('_', ' ')}</span>
                    {sv.frequency && (
                      <span style={{ fontSize: '11px', color: '#3b82f6' }}>{sv.frequency}</span>
                    )}
                    <span style={{ fontSize: '12px', color: '#888', flex: 1 }}>{sv.reason}</span>
                    {sv.confidence && (
                      <span title="How much data backs this verdict" style={{ fontSize: '10px', color: sv.confidence === 'high' ? '#22c55e' : sv.confidence === 'low' ? '#f59e0b' : '#888' }}>{sv.confidence} confidence</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Session Insights */}
        {ai.session_insights?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#aaa', fontWeight: '600', marginBottom: '8px' }}>Session insights</div>
            {ai.session_insights.map((si, i) => {
              const typeColors = { highlight: '#22c55e', warning: '#f59e0b', pattern: '#3b82f6' };
              return (
                <div key={i} style={{ padding: '8px 12px', background: '#1a1a1a', borderRadius: '6px', marginBottom: '4px', fontSize: '13px', color: '#ccc', borderLeft: `3px solid ${typeColors[si.type] || '#555'}` }}>
                  {si.insight}
                </div>
              );
            })}
          </div>
        )}

        {/* Rotation + Portfolio + Timing */}
        {ai.rotation_advice && (
          <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', marginBottom: '8px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a' }}>
            <span style={{ color: '#f59e0b', fontSize: '11px' }}>Rotation: </span>{ai.rotation_advice}
          </div>
        )}
        {ai.portfolio_health && (
          <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', marginBottom: '8px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a' }}>
            <span style={{ color: '#3b82f6', fontSize: '11px' }}>Portfolio: </span>{ai.portfolio_health}
          </div>
        )}
        {ai.audience_overlap_warnings?.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            {ai.audience_overlap_warnings.map((w, i) => (
              <div key={i} style={{ padding: '8px 12px', background: '#1a1a0a', borderRadius: '6px', marginBottom: '4px', fontSize: '12px', color: '#f59e0b', borderLeft: '3px solid #f59e0b' }}>
                {w}
              </div>
            ))}
          </div>
        )}
        {ai.timing_recommendation && (
          <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a' }}>
            <span style={{ color: '#666', fontSize: '11px' }}>Best posting window: </span>{ai.timing_recommendation}
          </div>
        )}

        {/* Breakout Analysis */}
        {ai.breakout_analysis && (
          <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a' }}>
            <span style={{ color: '#666', fontSize: '11px' }}>Breakouts: </span>{ai.breakout_analysis}
          </div>
        )}
      </div>
    );
  };
  // ─── Discovery Tab ───

  // Content is about the CREATOR (the vault is shared by every account), so
  // no account is needed here.
  const generateContentReport = async () => {
    setContentLoading(true);
    try {
      const res = await axios.post(`${API}/api/reports/content`, { creator_id: creatorId });
      setContentReport({ ...res.data, _created_at: res.data?.generated_at || new Date().toISOString() });
      setContentReportCount(c => Math.max(c, 1)); // clears the Discovery/Action prereq nudge
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
    loadReady();
    setContentLoading(false);
  };

  const loadContentLatest = async () => {
    const reqCreator = creatorId;
    try {
      const res = await axios.get(`${API}/api/reports/performance/history?creator_id=${creatorId}&report_type=content&limit=1`);
      if (res.data?.[0]?.id) {
        const detail = await axios.get(`${API}/api/reports/performance/detail/${res.data[0].id}`);
        if (detail.data?.report_data && reqCreator === creatorRef.current) {
          setContentReport({ ...detail.data.report_data, _created_at: detail.data.created_at, _period_from: detail.data.period_from, _period_to: detail.data.period_to });
        }
      }
    } catch (err) { console.error('Content load error:', err); }
  };

  const renderContentTab = () => {
    const cr = contentReport?.ai_analysis;
    return (
      <div>
        {(cr?.insufficient_data || cr?.error) && (
          <div style={{ padding: '12px', background: '#1a1a0a', border: '1px solid #b45309', borderRadius: '8px', color: '#f59e0b', fontSize: '13px', marginBottom: '12px' }}>
            {cr.insufficient_data ? cr.message : `The AI analysis failed: ${cr.error}`}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span title="The vault belongs to the creator, so this report pools the posts of all the creator's accounts. Discovery uses it for every account."
            style={{ fontSize: '12px', color: '#f59e0b', background: '#1a1408', border: '1px solid #3f3410', borderRadius: '8px', padding: '4px 10px' }}>
            👤 Whole creator — all {(accounts || []).length > 1 ? `${accounts.length} accounts` : 'accounts'} pooled
          </span>
          <span style={{ marginLeft: 'auto' }}>{reportStamp(contentReport?._created_at, contentReport?._period_from || contentReport?.period?.from, contentReport?._period_to || contentReport?.period?.to, 'No Content report for this creator yet')}</span>
          <button onClick={generateContentReport} disabled={contentLoading || readiness('content')[0]}
            title={readiness('content')[2] || ''}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: contentLoading ? 'wait' : readiness('content')[0] ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', background: contentLoading || readiness('content')[0] ? '#333' : '#f59e0b', color: readiness('content')[0] ? '#888' : '#fff' }}>
            {contentLoading ? '🔄 Analyzing...' : `📸 Generate Content Report${readiness('content')[1]}`}
          </button>
          <button onClick={loadContentLatest} disabled={contentLoading}
            style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #333', cursor: 'pointer', fontSize: '12px', background: 'transparent', color: '#888' }}>
            Load Latest
          </button>
        </div>

        {readiness('content')[2] && !contentLoading && (
          <div style={{ fontSize: '11px', color: readiness('content')[0] ? '#888' : '#f59e0b', margin: '-8px 0 12px' }}>{readiness('content')[2]}</div>
        )}

        {contentLoading && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#f59e0b', marginBottom: '8px' }}>Analyzing content performance + vault...</div>
            <div style={{ fontSize: '13px', color: '#666' }}>Matching content descriptions to performance data. 15-30 seconds.</div>
          </div>
        )}

        {!cr && !contentLoading && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#555' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📸</div>
            <div style={{ fontSize: '15px', marginBottom: '8px' }}>Content Analysis</div>
            <div style={{ fontSize: '13px', color: '#444' }}>Analyzes which content types perform best, creates a content profile, and maps your vault inventory for Discovery.</div>
          </div>
        )}

        {cr?.error && <div style={{ padding: '16px', background: '#2a0a0a', borderRadius: '8px', color: '#ef4444', fontSize: '12px' }}>Error: {cr.error}</div>}

        {cr && !cr.error && (
          <div>
            {cr.content_profile && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '700', marginBottom: '8px' }}>📸 Content Profile</div>
                <div style={{ padding: '12px 16px', background: '#1a1a1a', borderRadius: '10px', border: '1px solid #2a2a2a', fontSize: '13px', color: '#ccc', lineHeight: '1.6' }}>{cr.content_profile.summary}</div>
                {cr.content_profile.strengths?.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#22c55e' }}>Strengths:</span>
                    {cr.content_profile.strengths.map((s, i) => <span key={i} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '8px', background: '#0a1a0a', color: '#22c55e', border: '1px solid #22c55e40' }}>{s}</span>)}
                  </div>
                )}
                {cr.content_profile.weaknesses?.length > 0 && (
                  <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#ef4444' }}>Gaps:</span>
                    {cr.content_profile.weaknesses.map((w, i) => <span key={i} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '8px', background: '#1a0a0a', color: '#ef4444', border: '1px solid #ef444440' }}>{w}</span>)}
                  </div>
                )}
              </div>
            )}

            {cr.top_performing_themes?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '700', marginBottom: '8px' }}>🏆 Top Performing Themes</div>
                {cr.top_performing_themes.map((t, i) => (
                  <div key={i} style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #2a2a2a', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>{t.theme}</span>
                      <span style={{ fontSize: '11px', color: '#3b82f6' }}>{t.avg_views?.toLocaleString()} avg views</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{t.why_it_works}</div>
                    {t.best_subreddits?.length > 0 && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Best in: {t.best_subreddits.join(', ')}</div>}
                  </div>
                ))}
              </div>
            )}

            {cr.content_subreddit_matrix?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#ff4500', fontWeight: '700', marginBottom: '8px' }}>🎯 Content × Subreddit Fit</div>
                {cr.content_subreddit_matrix.map((m, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: '#1a1a1a', borderRadius: '6px', marginBottom: '4px', border: '1px solid #222' }}>
                    <span style={{ color: '#ff4500', fontWeight: '600', fontSize: '13px' }}>{m.subreddit}</span>
                    <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>→ {m.best_content_type}</span>
                    {m.tip && <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>💡 {m.tip}</div>}
                    {m.avoid && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>⚠ Avoid: {m.avoid}</div>}
                  </div>
                ))}
              </div>
            )}

            {cr.media_type_insights && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#0a0a1a', borderRadius: '8px', border: '1px solid #1d4ed840', fontSize: '12px', color: '#888' }}>🎬 {cr.media_type_insights}</div>
            )}

            {cr.vault_analysis && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#8b5cf6', fontWeight: '700', marginBottom: '8px' }}>📦 Vault: <span style={{ color: cr.vault_analysis.health === 'good' ? '#22c55e' : cr.vault_analysis.health === 'moderate' ? '#f59e0b' : '#ef4444' }}>{cr.vault_analysis.health}</span></div>
                <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #2a2a2a', fontSize: '12px', color: '#888' }}>{cr.vault_analysis.summary}</div>
                {cr.vault_analysis.recommendations?.length > 0 && (
                  <div style={{ marginTop: '8px' }}>{cr.vault_analysis.recommendations.map((r, i) => <div key={i} style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>→ {r}</div>)}</div>
                )}
              </div>
            )}

            {cr.content_map_for_discovery && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#22c55e', fontWeight: '700', marginBottom: '8px' }}>🗺 Content Map (feeds Discovery)</div>
                <div style={{ padding: '10px 14px', background: '#0a1a0a', borderRadius: '8px', border: '1px solid #22c55e30', fontSize: '12px' }}>
                  <div style={{ color: '#888', marginBottom: '4px' }}><span style={{ color: '#22c55e' }}>Niches:</span> {cr.content_map_for_discovery.primary_niches?.join(', ')}</div>
                  <div style={{ color: '#888', marginBottom: '4px' }}><span style={{ color: '#22c55e' }}>Descriptors:</span> {cr.content_map_for_discovery.content_descriptors?.join(', ')}</div>
                  <div style={{ color: '#888' }}><span style={{ color: '#22c55e' }}>Audience:</span> {cr.content_map_for_discovery.audience_appeal}</div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    );
  };


  const generateDiscovery = async () => {
    if (!acct) { alert('Pick an account first — every report is about one account.'); return; }
    setDiscoveryLoading(true);
    setDiscoveryError(null);
    try {
      const body = { creator_id: creatorId };
      body.account_id = acct;
      if (selectedPerfReport) body.performance_report_id = selectedPerfReport;
      if (filtersTouched) body.discovery_filters = discoveryFilters; // else: the account's strategy
      const res = await axios.post(`${API}/api/discovery/discover`, body);
      setDiscoveryReport({ report_data: res.data, created_at: new Date().toISOString() });
    } catch (err) {
      setDiscoveryError(err.response?.data?.error || err.message);
    }
    setDiscoveryLoading(false);
  };

  const renderDiscoveryTab = () => {
    const rd = discoveryReport?.report_data || discoveryReport || {};
    const dr = rd.ai_analysis || null;
    const basedOn = rd.based_on_performance_report || discoveryReport?.report_data?.based_on_performance_report;

    return (
      <div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          {selectedAccount && <span style={{ fontSize: '13px', color: '#888' }}>u/{selectedAccount.reddit_username}</span>}
          {perfReports.length > 0 && (
            <select
              value={selectedPerfReport}
              onChange={e => setSelectedPerfReport(e.target.value)}
              style={{ fontSize: '12px', padding: '6px 10px', background: '#1a1a1a', color: '#aaa', border: '1px solid #333', borderRadius: '6px' }}
            >
              {perfReports.map(r => {
                const days = r.period_from && r.period_to
                  ? Math.round((new Date(r.period_to) - new Date(r.period_from)) / (1000*60*60*24))
                  : '?';
                const date = new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                return (
                  <option key={r.id} value={r.id}>
                    {date} · {days}d · {r.post_count || '?'} posts
                  </option>
                );
              })}
            </select>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              ['verified', '✅ Verified', 'Subs that require verification'],
              ['non_verified', '☑️ Non-verified', 'Subs without a verification requirement'],
              ['ai_tolerable', '🤖 AI tolerable', 'Leave out subs whose rules ban AI-generated content'],
              ['dating', '💞 Dating', 'Add dating / meetup / personals subs'],
            ].map(([k, label, tip]) => {
              const on = !!discoveryFilters[k];
              return (
                <button key={k} type="button" title={tip} onClick={() => toggleFilter(k)}
                  style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '14px', cursor: 'pointer',
                    background: on ? '#1e3a5f' : '#1a1a1a', color: on ? '#fff' : '#888',
                    border: `1px solid ${on ? '#3b82f6' : '#333'}` }}>
                  {on ? '✓ ' : ''}{label}
                </button>
              );
            })}
          </div>
          <div style={{ width: '100%', fontSize: '11px', color: '#888', marginTop: '2px' }}>
            {(() => {
              const f = discoveryFilters;
              const groups = f.verified && f.non_verified ? 'verified + non-verified subs' : f.verified ? 'verified subs only' : f.non_verified ? 'non-verified subs only' : null;
              const parts = [groups ? (f.dating ? `${groups} + dating subs` : groups) : 'dating subs only', f.ai_tolerable ? 'leaving out subs that ban AI content' : 'any AI policy'];
              return <>This run: <b style={{ color: '#ccc' }}>{parts.join(' · ')}</b>{filtersFromStrategy ? <span style={{ color: '#555' }}> — pre-filled from u/{selectedAccount?.reddit_username}'s {filtersFromStrategy === 'default' ? 'default settings' : 'strategy'}; changes apply to this run only</span> : null}</>;
            })()}
          </div>
          <span style={{ marginLeft: 'auto' }}>{reportStamp(discoveryReport?.created_at, null, null, 'No Discovery run for this account yet')}</span>
          <button onClick={generateDiscovery} disabled={discoveryLoading || !hasPerf}
            title={!hasPerf ? 'Generate a Performance report first' : ''}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: discoveryLoading ? 'wait' : (!hasPerf ? 'not-allowed' : 'pointer'), fontSize: '13px', fontWeight: '600', background: discoveryLoading ? '#333' : '#3b82f6', color: '#fff', opacity: !hasPerf ? 0.5 : 1 }}>
            {discoveryLoading ? '🔄 Discovering...' : '🔍 Run Discovery'}
          </button>
        </div>
        {(!hasPerf || !hasContent) && (
          <div style={{ padding: '12px', background: '#1a1a0a', border: '1px solid #f59e0b', borderRadius: '8px', color: '#f59e0b', fontSize: '13px', marginBottom: '16px', lineHeight: '1.5' }}>
            {!hasPerf
              ? <>⚠ <b>Run the Performance report first</b> — Discovery uses it as its data source. Order: Performance → Content → Discovery.</>
              : <>💡 <b>Tip:</b> run a <b>Content report</b> before Discovery — it matches new subs to your content niches. You can run Discovery now, but matching will be weaker without it.</>}
          </div>
        )}

        {discoveryError && <div style={{ padding: '12px', background: '#2a0a0a', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>{discoveryError}</div>}

        {discoveryLoading && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#3b82f6', marginBottom: '8px' }}>Analyzing 8,500 subreddits...</div>
            <div style={{ fontSize: '13px', color: '#666' }}>Checking moderator overlap, tag matching, content fit. This takes 30-60 seconds.</div>
          </div>
        )}

        {!discoveryLoading && dr && !dr.error && (
          <div>
            {/* Strategy Summary */}
            {dr.strategy_summary && (
              <div style={{ padding: '12px 16px', background: '#1a1a1a', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', color: '#ccc', lineHeight: '1.6', border: '1px solid #2a2a2a' }}>
                {dr.strategy_summary}
              </div>
            )}

            {basedOn && (
              <div style={{ fontSize: '11px', color: '#555', marginBottom: '12px', display: 'flex', gap: '12px' }}>
                <span>Based on: {new Date(basedOn.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} report</span>
                <span>{basedOn.post_count} posts</span>
                <span>{basedOn.period_from && basedOn.period_to ? Math.round((new Date(basedOn.period_to) - new Date(basedOn.period_from)) / (1000*60*60*24)) + ' days' : ''}</span>
              </div>
            )}

            {/* Existing subs are judged by the Performance tab now — Discovery
                only proposes NEW subs. A short reminder so the tab reads clearly. */}
            <div style={{ fontSize: '11px', color: '#555', marginBottom: '16px', fontStyle: 'italic' }}>
              Existing subs are judged in the Performance tab. Discovery focuses on new subreddits to add.
            </div>

            {/* Suggested Additions */}
            {['large_subs', 'medium_subs', 'small_niche'].map(tier => {
              const items = dr.suggested_additions?.[tier] || [];
              if (items.length === 0) return null;
              const labels = { large_subs: '🏢 Large subs (300K+ weekly)', medium_subs: '🏠 Medium subs (100–300K weekly)', small_niche: '🏡 Niche subs (<100K weekly)' };
              return (
                <div key={tier} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#aaa', fontWeight: '600', marginBottom: '8px' }}>{labels[tier]}</div>
                  {items.map((sub, i) => (
                    <div key={i} style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', marginBottom: '4px', border: '1px solid #222' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#ff4500', fontWeight: '600', fontSize: '14px' }}>{sub.subreddit}</span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: '#666' }}>{sub.size ? (sub.size >= 1000000 ? (sub.size/1000000).toFixed(1)+'M' : sub.size >= 1000 ? (sub.size/1000).toFixed(0)+'K' : sub.size) : '?'} weekly</span>
                          <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px', background: sub.priority === 'high' ? '#22c55e20' : sub.priority === 'medium' ? '#f59e0b20' : '#66666620', color: sub.priority === 'high' ? '#22c55e' : sub.priority === 'medium' ? '#f59e0b' : '#666' }}>{sub.priority}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '4px' }}>{sub.why}</div>
                      {sub.content_match && <div style={{ fontSize: '11px', color: '#3b82f6' }}>Content: {sub.content_match}</div>}
                      {sub.fit_note && (
                        <div title="Counted from the vault: fresh items the auto-picker would actually place in this sub under its rules"
                          style={{ fontSize: '11px', marginTop: '2px', color: sub.fit_items === 0 ? '#ef4444' : sub.fit_items < 10 ? '#f59e0b' : '#22c55e' }}>
                          🗂 {sub.fit_note}{sub.fit_items === 0 ? ' — auto-fill can\'t fill this sub yet' : ''}
                        </div>
                      )}
                      {sub.risk_notes && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>⚠ {sub.risk_notes}</div>}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Verification Recommendations */}
            {dr.verification_recommendations?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#aaa', fontWeight: '600', marginBottom: '8px' }}>✅ Worth getting verified in</div>
                {dr.verification_recommendations.map((sub, i) => (
                  <div key={i} style={{ padding: '10px 14px', background: '#0a1a0a', borderRadius: '8px', marginBottom: '4px', border: '1px solid #22c55e30' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: '#22c55e', fontWeight: '600' }}>{sub.subreddit}</span>
                      <span style={{ fontSize: '11px', color: '#666' }}>{sub.size ? (sub.size >= 1000000 ? (sub.size/1000000).toFixed(1)+'M' : (sub.size/1000).toFixed(0)+'K') : '?'} weekly</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#ccc' }}>{sub.why}</div>
                    {sub.effort && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Effort: {sub.effort}</div>}
                    {sub.fit_note && <div style={{ fontSize: '11px', marginTop: '2px', color: sub.fit_items === 0 ? '#ef4444' : sub.fit_items < 10 ? '#f59e0b' : '#22c55e' }}>🗂 {sub.fit_note}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Themes the brief asked for that no sub fits (no filler instead) */}
            {Array.isArray(dr.niches_not_found) && dr.niches_not_found.length > 0 && (
              <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a', marginBottom: '16px' }}>
                <span style={{ color: '#f59e0b', fontSize: '11px' }}>No sub found for: </span>{dr.niches_not_found.join(', ')}
              </div>
            )}
            {dr.size_mix_trimmed?.dropped?.length > 0 && (
              <div style={{ fontSize: '11px', color: '#777', marginBottom: '12px' }}>
                Size mix: {dr.size_mix_trimmed.dropped.length} more small sub{dr.size_mix_trimmed.dropped.length === 1 ? '' : 's'} left out ({dr.size_mix_trimmed.dropped.join(', ')}) — about a quarter of the suggestions are small
              </div>
            )}

            {/* Content Gaps */}
            {dr.content_gaps && (
              <div style={{ padding: '10px 14px', background: '#1a1a1a', borderRadius: '8px', fontSize: '13px', color: '#ccc', border: '1px solid #2a2a2a', marginBottom: '16px' }}>
                <span style={{ color: '#f59e0b', fontSize: '11px' }}>Content gaps: </span>{dr.content_gaps}
              </div>
            )}

            {/* Pool Stats */}
            {(rd.pool_stats || discoveryReport?.pool_stats) && (
              <div style={{ fontSize: '11px', color: '#444', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span>Pool: {(rd.pool_stats || discoveryReport.pool_stats).total_nsfw_subs} NSFW subs</span>
                <span>Assigned: {(rd.pool_stats || discoveryReport.pool_stats).already_assigned}</span>
                <span>Blocked (other accounts): {(rd.pool_stats || discoveryReport.pool_stats).blocked_by_other_accounts}</span>
                <span>Available: {(rd.pool_stats || discoveryReport.pool_stats).available_for_discovery}</span>
              </div>
            )}
          </div>
        )}

        {!discoveryLoading && !dr && !discoveryError && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#555' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
            <div style={{ fontSize: '15px', marginBottom: '8px' }}>Subreddit Discovery</div>
            <div style={{ fontSize: '13px', color: '#444' }}>Analyzes 8,500 subs against your performance data, content profile, and moderator overlap to find the best new subs for your rotation.</div>
          </div>
        )}
      </div>
    );
  };


  // ─── Action Plan Tab ───


  // ─── Modal ───
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 1001,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#111', borderRadius: '12px', border: '1px solid #2a2a2a',
        width: '95%', maxWidth: '900px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#ff4500' }}>AI Reports</div>
            <span style={{ fontSize: '12px', color: '#888' }}>for</span>
            <select value={acct || ''} onChange={e => setAcct(e.target.value || null)}
              title="Performance and Discovery are about one account; Content covers the whole creator"
              style={{ fontSize: '13px', padding: '4px 8px', background: '#1a1a1a', color: acct ? '#fff' : '#f59e0b', border: `1px solid ${acct ? '#333' : '#b45309'}`, borderRadius: '6px' }}>
              {!acct && <option value="">— pick an account —</option>}
              {(accounts || []).map(a => <option key={a.id} value={a.id}>u/{a.reddit_username}</option>)}
            </select>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>

        {jobStatus && (() => {
          const fmt = (m) => !m ? 'never' : `${m.status === 'ok' ? '✓' : m.status === 'running' ? '⏳' : '⚠'} ${new Date(m.finished_at || m.started_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}${m.status === 'failed' ? ` — failed: ${m.error}` : m.status === 'skipped' ? ` — skipped: ${m.summary || ''}` : ''}`;
          const bad = (m) => !m || ['failed', 'skipped'].includes(m.status);
          return (
            <div style={{ padding: '6px 20px', fontSize: '11px', color: '#777', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: '16px', flexWrap: 'wrap' }}
              title="No report is made automatically: after each day's click sync the Planner shows a 'Run the report' card for every account whose next 7 days are ready, and a person runs it.">
              <span style={{ color: bad(jobStatus.link_sync) ? '#f59e0b' : '#777' }}>🔄 Click sync (daily 05:00 UTC): {fmt(jobStatus.link_sync)}</span>
              <span style={{ color: jobStatus.report_cards?.status === 'failed' ? '#f59e0b' : '#777' }}>📊 Report cards after the sync: {fmt(jobStatus.report_cards)}</span>
              <span style={{ color: jobStatus.monday_check?.status === 'failed' ? '#f59e0b' : '#777' }}>📅 Monday check (no AI): {fmt(jobStatus.monday_check)}</span>
            </div>
          );
        })()}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #2a2a2a', padding: '0 16px' }}>
          <button onClick={() => setActiveTab('performance')} style={tabStyle(activeTab === 'performance')}>1 · Performance</button>
          <button onClick={() => setActiveTab('content')} style={tabStyle(activeTab === 'content')}>2 · Content</button>
          <button onClick={() => setActiveTab('discovery')} title={!hasPerf ? 'Run Performance first' : ''} style={{ ...tabStyle(activeTab === 'discovery'), opacity: hasPerf ? 1 : 0.5 }}>3 · Discovery</button>
          <button onClick={() => setActiveTab('action')} style={tabStyle(activeTab === 'action')}>💡 Suggestions</button>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {activeTab === 'action' && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '13px', lineHeight: 1.7 }}>
              <div style={{ fontSize: '16px', color: '#fff', fontWeight: 600, marginBottom: '8px' }}>💡 Suggestions now live in the Planner</div>
              Every report proposes changes there — remove, post more/less often, rest, verify, run Discovery — and nothing changes until someone accepts.<br/>
              Open <strong style={{ color: '#ff4500' }}>Scheduler → 📋 Planner</strong> for u/{selectedAccount?.reddit_username || '…'} to review this week's suggestions.
            </div>
          )}
          {activeTab === 'performance' && renderPerformanceReport()}
          {activeTab === 'content' && renderContentTab()}
          {activeTab === 'discovery' && renderDiscoveryTab()}
        </div>
      </div>
    </div>
  );
}

export default AIReportsModal;
